/**
 * LocalStorage utility con supporto per prefisso VITE_PREFIX
 *
 * Tutte le chiavi salvate in localStorage vengono automaticamente prefissate
 * con il valore di VITE_PREFIX (se definito) per evitare conflitti tra
 * diverse istanze dell'applicazione.
 */

/**
 * Ottiene la chiave con prefisso applicato
 */
function getPrefixedKey(key: string): string {
    const prefix = import.meta.env.VITE_PREFIX || "";
    return prefix ? `${prefix}_${key}` : key;
}

/**
 * Verifica se siamo in un ambiente browser (non SSR)
 */
function isBrowser(): boolean {
    return typeof window !== "undefined";
}

/**
 * Utility per gestire localStorage con prefisso VITE_PREFIX
 */
export const storage = {
    /**
     * Ottiene un valore dal localStorage
     * @param key - Chiave senza prefisso
     * @returns Valore salvato o null se non esiste
     */
    getItem(key: string): string | null {
        if (!isBrowser()) {
            return null;
        }
        try {
            const prefixedKey = getPrefixedKey(key);
            return localStorage.getItem(prefixedKey);
        } catch (error) {
            console.error("Error reading from localStorage:", error);
            return null;
        }
    },

    /**
     * Salva un valore nel localStorage
     * @param key - Chiave senza prefisso
     * @param value - Valore da salvare
     */
    setItem(key: string, value: string): void {
        if (!isBrowser()) {
            return;
        }
        try {
            const prefixedKey = getPrefixedKey(key);
            localStorage.setItem(prefixedKey, value);
        } catch (error) {
            console.error("Error writing to localStorage:", error);
        }
    },

    /**
     * Rimuove un valore dal localStorage
     * @param key - Chiave senza prefisso
     */
    removeItem(key: string): void {
        if (!isBrowser()) {
            return;
        }
        try {
            const prefixedKey = getPrefixedKey(key);
            localStorage.removeItem(prefixedKey);
        } catch (error) {
            console.error("Error removing from localStorage:", error);
        }
    },

    /**
     * Rimuove più chiavi dal localStorage
     * @param keys - Array di chiavi senza prefisso
     */
    removeItems(keys: string[]): void {
        keys.forEach((key) => this.removeItem(key));
    },
};
